/**
 * capital-weighted Pipeline IRR helpers.
 *
 * conversation-model-versioning Phase 5 Session 9 (2026-05-17):
 * `capitalWeightedIrr` implements spec §8 "Σ(IRR×Basis)/Σ(Basis)", one decimal.
 *
 * §13.C footgun: the STRONG `parseBasisUsd` (handles B/M/K suffixes,
 * Number.isFinite guard, returns 0 for null/undefined/NaN) is the canonical
 * basis parser for the whole web app.  The WEAK `parseBasis` in
 * `components/pipeline/PipelineSummary.tsx` must never be used for
 * aggregation — it has no B-suffix support and no finite guard.
 *
 * `parseBasisUsd` was previously module-local in `cockpit/PipelineMap.tsx`.
 * It is lifted here (option a) so `<PipelineMap>` imports from this file
 * and there is exactly ONE implementation.  No third copy is ever acceptable.
 */

/**
 * Parse a basis string like `"$45M"` / `"$1.2B"` / `"$120K"` / `"—"` /
 * `undefined` / `null` into a numeric USD value for aggregation.
 *
 * Rules (§13.C verbatim):
 * - Strip `[$,\s]`.
 * - Detect a trailing B / M / K suffix (case-insensitive).
 * - `parseFloat` the numeric part.
 * - `Number.isFinite` guard — NaN / Infinity → 0.
 * - `"—"` / empty string / null / undefined → 0.
 *
 * @example parseBasisUsd("$1.2B") === 1_200_000_000
 * @example parseBasisUsd("$45M")  === 45_000_000
 * @example parseBasisUsd("$120K") === 120_000
 * @example parseBasisUsd("—")     === 0
 * @example parseBasisUsd(undefined) === 0
 */
export function parseBasisUsd(basis: string | null | undefined): number {
  if (!basis) return 0;
  const stripped = basis.replace(/[$,\s]/g, '');
  const suffix = stripped.slice(-1).toUpperCase();
  const numericPart =
    suffix === 'M' || suffix === 'B' || suffix === 'K'
      ? stripped.slice(0, -1)
      : stripped;
  const n = parseFloat(numericPart);
  if (!Number.isFinite(n)) return 0;
  if (suffix === 'B') return n * 1e9;
  if (suffix === 'M') return n * 1e6;
  if (suffix === 'K') return n * 1e3;
  return n;
}

/**
 * Capital-weighted Pipeline IRR: `Σ(IRR×Basis) / Σ(Basis)`.
 *
 * A deal contributes to the weighted average ONLY when:
 *  1. Its parsed `irr` is a finite number (via `Number()` + `Number.isFinite`).
 *  2. Its parsed `basis` is > 0 (the weight — zero-basis deals can't contribute).
 *
 * @param deals - Array of deal snapshots (supply the filtered subset).
 * @returns Weighted IRR formatted to one decimal place, e.g. `"18.4%"`.
 *          Returns `"—"` when no deal contributes a finite weight.
 *
 * spec §8 / ADR 0018 BLOCKING invariant: weight and displayed IRR MUST come
 * from the SAME `displayVersion` snapshot per row — callers must pass the
 * same source for both `irr` and `basis` (i.e. `deal.irr` and `deal.basis`
 * from `conversationToDeal`, which already resolves both from `displayVersion`).
 */
export function capitalWeightedIrr(
  deals: { irr: string | null | undefined; basis: string | null | undefined }[]
): string {
  let weightedSum = 0;
  let totalWeight = 0;

  for (const d of deals) {
    const weight = parseBasisUsd(d.basis);
    if (weight <= 0) continue; // zero-basis deals don't contribute

    // Guard null/undefined/empty before Number() coercion:
    // Number(null) === 0, Number('') === 0 — both are finite but should be
    // treated as "no IRR value", not as 0% IRR.
    if (d.irr == null || d.irr === '' || d.irr === '—') continue;
    // Strip trailing % if present so Number() resolves correctly.
    const irrRaw = d.irr.replace(/%$/, '');
    if (irrRaw === '' || irrRaw === '—') continue;
    const irrNum = Number(irrRaw);
    if (!Number.isFinite(irrNum)) continue; // non-numeric IRR — skip

    weightedSum += irrNum * weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) return '—';

  const weighted = weightedSum / totalWeight;
  return `${weighted.toFixed(1)}%`;
}
